import React from "react";
import { Content } from "@/models/document";
import { Z_INDEX, zIndexClass } from "@/utils/z-index";
import { Card, CardBody, Chip, Typography, Divider } from "@/components/ui";
import { FaClock, FaGlobe, FaShare, FaHeart, FaComment, FaRetweet } from "react-icons/fa";
import useTranslation from "@/hooks/use-translation";
import { safeString } from "@/utils/safe-value";

interface TimelineViewProps {
  items: Content[];
  displayMode?: string;
}

const TimelineView: React.FC<TimelineViewProps> = ({
  items,
  displayMode = "normal",
}) => {
  const { t } = useTranslation();

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getAuthorInfo = (item: any) => {
    // If item has normalized, use normalized; otherwise use item directly
    const rawData = item.normalized ? item.normalized : item;
    return {
      username: safeString(rawData.author?.username || rawData.author?.title) || "Unknown",
      avatar: safeString(rawData.author?.media?.[0]?.url || rawData.author?.avatar),
      verified: rawData.author?.verified,
    };
  };

  const getContentInfo = (item: any) => {
    // If item has normalized, use normalized; otherwise use item directly
    const rawData = item.normalized ? item.normalized : item;
    return {
      title: safeString(rawData.title),
      text: safeString(rawData.text || rawData.description),
      date: safeString(rawData.date || rawData.published_time),
      language: safeString(rawData.language || rawData.lang || item.extra?.lang),
      url: safeString(rawData.url),
      stats: rawData.stats,
      media: rawData.media && rawData.media.length > 0
        ? rawData.media
        : rawData.image || rawData.thumbnail
          ? [
              {
                type: "image",
                url: safeString(rawData.image || rawData.thumbnail),
                thumbnail: safeString(rawData.thumbnail),
              },
            ]
          : [],
    };
  };

  const getStatsIcon = (key: string) => {
    switch (key) {
      case "likes":
        return <FaHeart className="w-4 h-4" />;
      case "comments":
        return <FaComment className="w-4 h-4" />;
      case "shares":
        return <FaRetweet className="w-4 h-4" />;
      default:
        return null;
    }
  };

  // Trier les éléments par date
  const sortedItems = [...items].sort((a, b) => {
    const dateA = new Date(getContentInfo(a).date || 0).getTime();
    const dateB = new Date(getContentInfo(b).date || 0).getTime();
    return dateB - dateA; // Plus récent en premier
  });

  return (
    <div className="max-w-4xl mx-auto">
      <div className="relative">
        {/* Ligne de timeline */}
        <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-arc-border"></div>

        <div className="space-y-8">
          {sortedItems.map((item, index) => {
            const authorInfo = getAuthorInfo(item);
            const contentInfo = getContentInfo(item);

            return (
              <div key={item.id || index} className="relative flex">
                {/* Point de timeline */}
                <div className={`absolute left-6 w-4 h-4 bg-accent rounded-full border-4 border-white shadow-arc-lg ${zIndexClass(Z_INDEX.BASE.OVERLAY)}`}></div>

                {/* Contenu */}
                <div className="ml-16 flex-1">
                  <Card
                    className="hover:shadow-arc-lg transition-all duration-300 cursor-pointer"
                    onClick={() =>
                      contentInfo.url && window.open(contentInfo.url, "_blank")
                    }
                  >
                    <CardBody className="p-6">
                      {/* Header avec auteur et date */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full overflow-hidden bg-surface-secondary">
                            {authorInfo.avatar ? (
                              <img
                                src={authorInfo.avatar}
                                alt={authorInfo.username}
                                className="size-full object-cover"
                              />
                            ) : (
                              <div className="size-full flex items-center justify-center text-content-tertiary text-lg font-semibold">
                                {authorInfo.username.charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="flex items-center space-x-2">
                              <Typography variant="text-sm" className="font-semibold">
                                {authorInfo.username}
                              </Typography>
                              {authorInfo.verified && (
                                <Chip size="sm" variant="soft" color="info">✓</Chip>
                              )}
                            </div>
                            <div className="flex items-center space-x-2 text-content-tertiary">
                              <FaClock className="w-4 h-4" />
                              <Typography variant="text-xs">
                                {contentInfo.date &&
                                  formatDate(contentInfo.date)}
                              </Typography>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Média */}
                      {contentInfo.media && contentInfo.media.length > 0 && (
                        <div className="mb-4">
                          <div className="relative rounded-arc-lg overflow-hidden bg-surface-secondary h-48">
                            <img
                              src={
                                contentInfo.media[0].url ||
                                contentInfo.media[0].thumbnail
                              }
                              alt={contentInfo.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>
                      )}

                      {/* Contenu */}
                      <div className="mb-4">
                        {contentInfo.title && (
                          <Typography variant="h6" className="font-semibold mb-2">
                            {contentInfo.title}
                          </Typography>
                        )}
                        <Typography variant="text-sm" className="leading-relaxed" color="secondary">
                          {contentInfo.text}
                        </Typography>
                      </div>

                      <Divider className="my-4" />

                      {/* Footer avec stats et métadonnées */}
                      <div className="flex items-center justify-between">
                        {/* Statistiques */}
                        {contentInfo.stats && (
                          <div className="flex items-center space-x-4">
                            {Object.entries(contentInfo.stats).map(
                              ([key, value]) => {
                                if (value === undefined || value === null) return null;
                                return (
                                  <div
                                    key={key}
                                    className="flex items-center space-x-1"
                                  >
                                    {getStatsIcon(key)}
                                    <Typography variant="text-sm" className="text-content-secondary font-medium">
                                      {typeof value === "number"
                                        ? value.toLocaleString()
                                        : String(value)}
                                    </Typography>
                                    <Typography variant="text-xs" className="text-content-tertiary capitalize">
                                      {key}
                                    </Typography>
                                  </div>
                                );
                              }
                            )}
                          </div>
                        )}

                        {/* Métadonnées */}
                        <div className="flex items-center space-x-4 text-sm text-content-secondary">
                          {contentInfo.language && (
                            <div className="flex items-center space-x-1">
                              <FaGlobe className="w-4 h-4" />
                              <span>{contentInfo.language.toUpperCase()}</span>
                            </div>
                          )}
                          {contentInfo.url && (
                            <div className="flex items-center space-x-1 text-accent">
                              <FaShare className="w-4 h-4" />
                              <span>{t("view_original")}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TimelineView;
